import React, { useContext, useEffect, useState } from 'react';
import * as ClientApi from '~/lib/ClientApi';
import Modal from '~/components/Modal';
import Loading from '~/components/Loading';
import { formatDateShort, formatDateTime } from '~/lib/DateTime';
import { useClientHistoryQuery } from '~/public/lib/PublicQueries';
import SideNavPicker from '~/components/SideNavPicker';
import EventDateAndTime from '~/components/calendar/EventDateAndTime.jsx';
import StartEndDateTime from '~/components/forms/StartEndDateTime.jsx';
import ThemedForm from '~/components/forms/ThemedForm.jsx';
import { FormContext } from '~/components/forms/FormContext.jsx';
import { StatusBadge } from '~/components/packages/GrantList.jsx';
import { humanGrantRedeemableAmount } from '~/lib/PackageHelpers.js';
import SubscriptionHistory from '~/components/forms/questions/components/ClientAccount/SubscriptionHistory';
import PackageHistory from '~/components/forms/questions/components/ClientAccount/PackageHistory';
import EventHistory from '~/components/forms/questions/components/ClientAccount/EventHistory';
import ClientAccountBilling from '~/components/forms/questions/components/ClientAccount/ClientAccountBilling';
import ThemedModal from '~/components/ThemedModal';
import WaitlistHistory from '~/components/forms/questions/components/ClientAccount/WaitlistHistory';
import InvoiceHistory from '~/components/forms/questions/components/ClientAccount/InvoiceHistory';
import {
    ArchiveBoxIcon,
    ArrowPathIcon,
    CalendarDaysIcon,
    ChevronLeftIcon,
    CurrencyDollarIcon,
    DocumentTextIcon,
    QueueListIcon,
} from '@heroicons/react/24/outline';
import { useIsMobile } from '~/hooks/use-mobile';

const AccountHeading = ({ email }) => (
    <div>
        <h3 className="text-xl">Manage Account</h3>
        {!!email && <div className="text-gray-500">{email}</div>}
    </div>
);

const ClientHistory = ({ history, refetch, client, allClients }) => {
    const isMobile = useIsMobile();
    // On mobile the navigation is shown on its own until a section is picked
    const [section, setSection] = useState(isMobile ? null : 'appointments');

    useEffect(() => {
        // A section is always selected once there is room for the side nav
        if (!isMobile && !section) {
            setSection('appointments');
        }
    }, [isMobile]);

    const navOptions = [
        {
            label: 'Appointments',
            value: 'appointments',
            icon: CalendarDaysIcon,
        },
        { label: 'Packages', value: 'packages', icon: ArchiveBoxIcon },
    ];

    if (history?.subscriptions?.length > 0) {
        navOptions.push({
            label: 'Subscriptions',
            value: 'subscriptions',
            icon: ArrowPathIcon,
        });
    }

    if (history?.waitlist?.length > 0) {
        navOptions.push({
            label: 'Waitlist',
            value: 'waitlist',
            icon: QueueListIcon,
        });
    }

    if (history?.invoices?.length > 0) {
        navOptions.push({
            label: 'Invoices',
            value: 'invoices',
            icon: DocumentTextIcon,
        });
    }

    navOptions.push({
        label: 'Billing',
        value: 'billing',
        icon: CurrencyDollarIcon,
    });

    const email = history?.user?.email;
    const selectedOption = navOptions.find(opt => opt.value === section);

    const sectionContent = (
        <>
            {section === 'appointments' && (
                <EventHistory events={history?.events || []} />
            )}

            {section === 'packages' && (
                <PackageHistory packages={history?.packages || []} />
            )}

            {section === 'subscriptions' && (
                <SubscriptionHistory packages={history?.subscriptions || []} />
            )}

            {section === 'waitlist' && (
                <WaitlistHistory waitlist={history?.waitlist || []} />
            )}

            {section === 'invoices' && (
                <InvoiceHistory invoices={history?.invoices || []} />
            )}

            {section === 'billing' && (
                <ClientAccountBilling
                    paymentMethods={history?.paymentMethods}
                    refetch={refetch}
                    client={client}
                />
            )}
        </>
    );

    if (isMobile) {
        if (!selectedOption) {
            return (
                <div>
                    <AccountHeading email={email} />
                    <SideNavPicker
                        options={navOptions}
                        value={section}
                        onChange={setSection}
                        variant="list"
                        className="mt-6"
                    />
                </div>
            );
        }

        return (
            <div>
                <button
                    className="flex items-center mb-6 cursor-pointer"
                    onClick={() => setSection(null)}
                >
                    <ChevronLeftIcon className="w-6 h-6 mr-2 shrink-0 text-primary-700" />
                    <h2 className="!text-left">{selectedOption.label}</h2>
                </button>

                {sectionContent}
            </div>
        );
    }

    return (
        <div className="flex items-stretch">
            <div className="w-[200px] shrink-0 border-r pr-4 mr-6">
                <AccountHeading email={email} />

                <SideNavPicker
                    options={navOptions}
                    value={section}
                    onChange={setSection}
                    className="mt-6"
                />
            </div>

            <div className="flex-1 min-w-0">
                {!!selectedOption && (
                    <h3 className="mb-4 text-xl">{selectedOption.label}</h3>
                )}
                {sectionContent}
            </div>
        </div>
    );
};

const ClientAccountManageModal = ({
    isOpen,
    onClose,
    client,
    allClients,
    form,
}) => {
    const { data, isLoading, refetch } = useClientHistoryQuery();
    const history = data || {};

    return (
        <ThemedModal
            isOpen={isOpen}
            onClose={onClose}
            panelClassName="!bg-gray-100"
            widthClass="sm:max-w-[750px]"
        >
            {isLoading && (
                <>
                    <h2 className="mb-6 !text-left">Manage Account</h2>
                    <Loading />
                </>
            )}
            {!isLoading && (
                <ClientHistory
                    history={history}
                    refetch={refetch}
                    allClients={allClients}
                    client={client}
                />
            )}
        </ThemedModal>
    );
};

export default ClientAccountManageModal;
